import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormArray,
  FormArray,
  ReactiveFormsModule,
  FormsModule,
  FormGroup
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  debounceTime,
  fromEvent,
  map,
  distinctUntilChanged,
  Observable,
  OperatorFunction,
  switchMap,
  startWith,
  pairwise
} from 'rxjs';
import { FormInputConfig, FormValidation, AllInputTypes } from '../../../interfaces/form-input-config';
import { Location } from '../../../interfaces/location';
import { RequestBaseService } from '../../../services/http-base/request-base.service';
import { LiveTranslateService } from '../../../services/live-translate.service';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { FormSubscriptionsService } from '../../../services/form-subscriptions.service';
import { FileUploadService } from '../../../services/file-upload.service';
import { CommonModule } from '@angular/common';
import { TimePickerComponent } from '../../../components/time-picker/time-picker.component';
import { DateRangeComponent } from '../../../components/date-range/date-range.component';
import { CustomYearMonthPickerComponent } from '../../../components/custom-year-month-picker/custom-year-month-picker.component';
import { RangeYearMonthPickerComponent } from '../../../components/range-year-month-picker/range-year-month-picker.component';
import { MonthDatePickerComponent } from '../../../components/month-date-picker/month-date-picker.component';
import { CalendarModule } from 'primeng/calendar';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { BadgeModule } from 'primeng/badge';
import { ProgressBarModule } from 'primeng/progressbar';
import { ToastModule } from 'primeng/toast';
import { DropDragFilesComponent } from '../../drop-drag-files/drop-drag-files.component';
import { URLS } from '../../../constants/URLS';
import { InputNumberModule } from 'primeng/inputnumber';
import { SkeletonModule } from 'primeng/skeleton';
import { ToastService } from '../../toasters/Toast.service';
import { DropdownModule } from 'primeng/dropdown';
import { PublicService } from '../../../services/public.service';
import { MultiSelectModule } from 'primeng/multiselect';
import {
  handleApiErrors,
  createValidationError
} from '../../../../../common/core/data-access/http-client/interceptors/error.interceptor';
import { countriesInfo } from '../../../../../common/core/primitives/phone-numbers/data/phone-number-flags.constants';
import {
  ICountryInfo,
  flagSourceUrl
} from '../../../../../common/core/primitives/phone-numbers/models/phone-number-flag.model';
import { ErrorOrError } from '../../../../../common/core/results/error-or/models/error-or-error.model';
import { Logger } from '../../../../../common/core/utilities/logging/logger';

import { TreeSelectModule } from 'primeng/treeselect';
import { TreeModule } from 'primeng/tree';
import { LocationModel, UserContextService, UtilityManagementPermissions } from '../../../../../common';
import { MapComponent } from '../map/map.component';

@Component({
  selector: 'app-form-field',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DropdownModule,
    MultiSelectModule,
    TranslateModule,
    FormsModule,
    NgSelectModule,
    MonthDatePickerComponent,
    TimePickerComponent,
    DateRangeComponent,
    CustomYearMonthPickerComponent,
    RangeYearMonthPickerComponent,
    CalendarModule,
    FloatLabelModule,
    SkeletonModule,
    FileUploadModule,
    ButtonModule,
    BadgeModule,
    ProgressBarModule,
    ToastModule,
    DropDragFilesComponent,
    InputNumberModule,
    TreeSelectModule,
    TreeModule,
    MapComponent
  ],
  providers: [FileUploadService],
  templateUrl: './form-field.component.html',
  styleUrl: './form-field.component.scss'
})
export class FormFieldComponent {
  totalSize: number = 0;

  todayDate: Date = new Date(); // Define today's date

  totalSizePercent: number = 0;
  onSelectScrollClose() {
    if (this.formItem.dynamicLoad && this.formItem.loadOnScroll && this.formItem.listValues?.length == 0) {
      this.scrollData.reset = true;
      this.getDynamicLoadData('', 1);
    }
  }
  scrollData: any = {
    reset: false,
    loading: false,
    currentPage: 1,
    totalPages: 2,
    totalCount: 0
  };
  activeToggle = false;

  @ViewChild('s') dynamicSelect?: NgSelectComponent;

  @Input() formItem: FormInputConfig = {
    type: AllInputTypes.Text,
    name: ''
  };

  // Add New Button Emit
  @Output() onAddNew: EventEmitter<FormInputConfig> = new EventEmitter<FormInputConfig>();

  // Define fileSizeLimit as an Input with a default value of 2MB
  @Input() fileSizeLimit: number = 1 * 1024 * 1024; // Default to 2MB if not provided

  @Input() formGroup: UntypedFormGroup = new UntypedFormGroup({});
  @Input() validationErrors: { [key: string]: string[] } = {};
  @Input() ListLoadChanged: boolean = false;
  @Input() crntFieldIndex: number = -1;
  @Output() execludeValue: EventEmitter<any> = new EventEmitter();
  @Output() includeValueInput: EventEmitter<any> = new EventEmitter();
  @Output() onLabelClick: EventEmitter<FormInputConfig> = new EventEmitter<FormInputConfig>();
  @Output() onAddFormArray: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onRemoveFormArray: EventEmitter<number> = new EventEmitter<number>();
  @Output() onGroupAddFormArray: EventEmitter<number> = new EventEmitter<number>();
  @Output() onGroupRemoveFormArray: EventEmitter<{
    index: number;
    groupIndex: number;
  }> = new EventEmitter<{ index: number; groupIndex: number }>();
  @Output() onFilterSelect: EventEmitter<{
    keyword: string;
    fieldName: string;
  }> = new EventEmitter<{ keyword: string; fieldName: string }>();
  @Output() onSelected: EventEmitter<any> = new EventEmitter<any>();
  @Input() files: any = {};
  @Output() onDownloadFile: EventEmitter<string> = new EventEmitter<string>();
  casheItems: any = {};
  formError = '';

  ckeConfig: any = {
    toolbarGroups: [
      { name: 'document', groups: ['mode', 'document', 'doctools'] },
      { name: 'clipboard', groups: ['clipboard', 'undo'] },
      {
        name: 'editing',
        groups: ['find', 'selection', 'spellchecker', 'editing']
      },
      { name: 'forms', groups: ['forms'] },
      '/',
      { name: 'basicstyles', groups: ['basicstyles', 'cleanup'] },
      {
        name: 'paragraph',
        groups: ['list', 'indent', 'blocks', 'align', 'bidi', 'paragraph']
      },
      { name: 'links', groups: ['links'] },
      '/',
      { name: 'styles', groups: ['styles'] },
      { name: 'colors', groups: ['colors'] },
      { name: 'tools', groups: ['tools'] },
      { name: 'others', groups: ['others'] },
      { name: 'about', groups: ['about'] }
    ],
    removeButtons:
      'Source,Save,NewPage,ExportPdf,Preview,Print,Templates,Form,Checkbox,Radio,TextField,Textarea,Select,Button,ImageButton,HiddenField,Image,About,SelectAll,Scayt,Iframe',
    extraPlugins:
      'easyimage,dialogui,dialog,a11yhelp,about,basicstyles,bidi,blockquote,clipboard,' +
      'button,panelbutton,panel,floatpanel,colorbutton,colordialog,menu,' +
      'contextmenu,dialogadvtab,div,elementspath,enterkey,entities,popup,' +
      'filebrowser,find,fakeobjects,flash,floatingspace,listblock,richcombo,' +
      'font,format,forms,horizontalrule,htmlwriter,iframe,indent,' +
      'indentblock,indentlist,justify,link,list,liststyle,magicline,' +
      'maximize,newpage,pagebreak,pastefromword,pastetext,preview,print,' +
      'removeformat,resize,save,menubutton,scayt,selectall,showblocks,' +
      'showborders,smiley,sourcearea,specialchar,stylescombo,tab,table,' +
      'tabletools,templates,toolbar,undo,wsc,wysiwygarea'
  };

  currentLang: string = 'en';

  cachedItem: any = {};
  id: any = new Date().getTime();
  cntryCode: any;
  private fileUploadService: FileUploadService = inject(FileUploadService);

  isDragging = false;
  savedSingleFile: any;
  isLoadingUploadFile: boolean = false;

  fileAddedBeforeArrayMuti: any = [];
  loading: boolean = false;

  // Start Mobile Number Field
  countryForm!: FormGroup;
  countries: ICountryInfo[] = countriesInfo;
  selectedCountry: ICountryInfo | null = null; // Default to the first country
  dropdownClasses: string[] = ['custom-dropdown']; // Add any dynamic classes if needed

  countryURLFlag(countryInfo: ICountryInfo): string {
    return `${flagSourceUrl}/${countryInfo.image}`;
  }
  // End Mobile Number Field

  hasCreateAttachmentsPermission: boolean =
    UserContextService && UtilityManagementPermissions
      ? UserContextService.hasPermission(UtilityManagementPermissions?.Attachments?.Create)
      : false;
  @ViewChild('fileUploader') fileUploader!: FileUpload;
  @ViewChild('fileInput') fileInput: ElementRef | undefined;

  constructor(
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private liveTranslate: LiveTranslateService,
    private _PublicService: PublicService,
    private cdr: ChangeDetectorRef,
    private dynamicLoadDataService: RequestBaseService,
    private elementRef: ElementRef,
    private formSubs: FormSubscriptionsService,
    private toastService: ToastService // private datePipe:DatePipe
  ) {
    // if (this.countries.length > 0) {
    //   this.countryForm = this.fb.group({
    //     selectedCountry: this.countries[0]
    //   });
    //   this.selectedCountry = this.countryForm?.value?.selectedCountry;
    // }
    // _PublicService.selectedCountry.next(this.selectedCountry);
  }

  async ngOnInit(): Promise<void> {
    this.countryForm = this.fb.group({
      selectedCountry: [null]
    });
    this.intializePhoneNumberLogic();

    if (this.formItem.type == AllInputTypes.ChooseFile && this.formItem.defaultValue) {
      if (this.formItem?.addedBforeItems?.id) {
        this.savedSingleFile = {
          id: this.formItem.addedBforeItems.id,
          name: this.formItem.addedBforeItems.name,
          size: this.formItem.addedBforeItems.size
        };
      }
    }

    if (this.formItem.type == AllInputTypes.DropFile && this.formItem.defaultValue) {
      this.fileAddedBeforeArrayMuti = this.formItem.addedBforeItems;
    }

    if (this.formItem.type == AllInputTypes.Auto_complete_text && this.formItem.defaultValue) {
      this.formGroup.get(this.formItem.name)?.setValue({
        label: this.formItem.defaultValue,
        value: this.formItem.defaultValue
      });
    }
    if (this.formItem.isUnique && this.formItem.defaultValue) {
      this.execludeValue.emit({
        index: this.crntFieldIndex,
        value: this.formItem.defaultValue,
        fieldName: this.formItem.name
      });
    }

    this.liveTranslate.getLangValues();
    this.translate.onLangChange.subscribe((event) => {
      this.currentLang = this.translate.currentLang;
    });
    this.currentLang = this.translate.currentLang;

    // to reflect change on the control which is responsable for loadOnchange inside on formArray inner controls
    if (this.formItem.type == 'array') {
      this.formItem.ArrayConfigration?.fields?.forEach((item) => {
        if (item.listLoad?.loadOnChange) {
          this.formGroup
            .get(item.listLoad?.loadOnChange)
            ?.valueChanges.pipe(debounceTime(300))
            .subscribe((value) => {
              if (value) {
                this.loadListData(item, value, this.formItem);
                (this.formGroup.get(this.formItem.name) as FormArray).controls.forEach((arrayItem) =>
                  arrayItem.get(item.name)?.setValue(null)
                );
              }
            });
        }
      });
    }

    if (
      this.formItem.dynamicLoad &&
      this.formItem.defaultValue &&
      this.formItem.dynamicLoad.defulatSelect &&
      this.formItem?.defaultValue == this.formGroup.get(this.formItem?.name)?.value
    ) {
      await this.getDynamicLoadData(this.formItem.dynamicLoad.defulatSelect);
      if (this.casheItems[this.formItem?.name] && this.casheItems[this.formItem?.name]?.classElement)
        this.casheItems[this.formItem?.name].classElement!.isOpen = false;
    } else if (this.formItem.dynamicLoad && this.formItem.loadOnScroll) {
      await this.getDynamicLoadData(this.formItem.dynamicLoad.defulatSelect);
    } else if (this.formItem.dynamicLoad && this.formItem.dynamicLoad.subData) {
      this.formItem.listValues = this.formItem?.dynamicLoad?.subData;
    }
    if (this.formItem.name == 'date') {
      // this.getDatetime()
    }
    if (this.formItem.listValues && this.formItem.listValues.length > 0) {
      if (this.formItem.isUnique) {
        this.formItem['listValuesCopy'] = this.validateList(this.formItem);
      } else {
        this.formItem.listValues = this.validateList(this.formItem);
      }
    }
    if (this.formItem['execludedValue']) {
      this.formSubs.includeValue.subscribe((data) => {
        this.includeValueInput.emit(data);
      });
    }
    if (this.formItem['execludedValue']) {
      this.formGroup
        .get(this.formItem.name)
        ?.valueChanges.pipe(startWith(null as any), pairwise())
        .subscribe(([prev, next]: [any, any]) => {
          this.execludeValue.emit({
            index: this.crntFieldIndex,
            value: next,
            fieldName: this.formItem.name,
            changedValue: prev != next && prev != null ? prev : null
          });
        });
    }

    if (this.formItem.type == 'select' && this.formItem.enableAddNewBtn) {
      let isProcessing = false; // Flag to prevent recursive updates

      this._PublicService.isAddNewBtnChanges.subscribe((res: any) => {
        if (isProcessing) {
          return; // Prevent processing when already handling the logic
        }

        if (res?.isChanged && this.formItem.name == res?.itemName) {
          isProcessing = true; // Set flag to indicate processing
          this.formGroup.get(this.formItem.name)?.setValue(res?.value);
          this._PublicService.isAddNewBtnChanges.next(null);
          isProcessing = false; // Reset flag after processing
        }
      });
    }

    // Set initial state of activeToggle based on form control's value
    if (this.formItem.type === 'toggleButton') {
      this.activeToggle = this.formGroup.get(this.formItem.name)?.value || false;
    }
  }
  private intializePhoneNumberLogic(): void {
  if (!this.countryForm || !this.countries?.length || !this.formItem) {
    console.warn('Dependencies not ready');
    return;
  }

  // Check if formItem is MobileNumber with code
  if (this.formItem.type === AllInputTypes.MobileNumber && this.formItem.mobileNumberCode) {
    const matchingCountry = this.countries.find(
      country => country.dial_code === this.formItem.mobileNumberCode
    );

    if (matchingCountry) {
      this.selectedCountry = matchingCountry;
      this.countryForm.get('selectedCountry')?.setValue(this.selectedCountry);
      this._PublicService.selectedCountry.next(this.selectedCountry);
    }
  }

  // Subscribe once to future changes in dropdown
  this.countryForm.get('selectedCountry')?.valueChanges.subscribe((country) => {
    if (country) {
      this.selectedCountry = country;
      this._PublicService.selectedCountry.next(this.selectedCountry);
    }
  });
}

  onFilter(e: any, name: string, items: any) {
    // if (items.length >= 10 || e.filter == '') {
    this.onFilterSelect.emit({
      keyword: e.filter,
      fieldName: name
    });
    // }
  }

  onSelectionChange(event: any, name: string) {
    this.onSelected.emit({
      item: event.itemValue,
      fieldName: name
    });
  }

  // Method to handle change of toggle button
  onToggleChange(event: any): void {
    this.activeToggle = event.target.checked;
  }

  // dublicate loadlistdata here to ensure that formarray inner controls work as expected when change value with (loadonchange way)
  async loadListData(formItem: FormInputConfig, value: any = null, formArray?: any) {
    try {
      if (formItem.listLoad) {
        // formItem.listLoad.loadService.setParams({ ...(formItem.listLoad.params ? formItem.listLoad.params : {}) })
        if (formItem.listLoad.prepareRequest) formItem.listLoad.prepareRequest(formItem.listLoad.loadService, value);
        // else
        //   formItem.listLoad.loadService.setPath(formItem.listLoad.url + `/${value}`);
        let res: any;
        formItem.listLoad?.type == 'get'
          ? (res = await formItem.listLoad.loadService.get({
              params: {
                ...(formItem.listLoad.params ? formItem.listLoad.params : {})
              },
              path: formItem.listLoad.loadOnChange ? formItem.listLoad.url + `/${value}` : formItem.listLoad.url,
              isLoad: !!formItem.listLoad.showLoader
            }))
          : (res = await formItem.listLoad.loadService.post({
              params: {
                ...(formItem.listLoad.params ? formItem.listLoad.params : {})
              },
              path: formItem.listLoad.loadOnChange ? formItem.listLoad.url + `/${value}` : formItem.listLoad.url,
              isLoad: !!formItem.listLoad.showLoader
            }));
        if (res[formItem.listLoad?.arrayAttributeName]) {
          if (res[formItem.listLoad?.arrayAttributeName].length) {
            formItem.listValues = [
              ...(formItem.listLoad.subData ?? []),
              ...(formItem.listLoad.responseDataPrepare
                ? formItem.listLoad.responseDataPrepare(res[formItem.listLoad?.arrayAttributeName])
                : res[formItem.listLoad?.arrayAttributeName].map((item: any) => {
                    return {
                      value: item[formItem.listLoad?.valueKey || ''],
                      label: item[formItem.listLoad?.labelKey || '']
                    };
                  }))
            ];
            if (formItem.isUnique) {
              formItem['listValuesCopy'] = formItem.listValues;
            }
            if (formItem.listLoad.getPreparedData) {
              formItem.listLoad.getPreparedData(formItem.listValues);
            }
            formItem['isOpen'] = true;
            if (this.casheItems[formItem.name] && this.casheItems[formItem.name].classElement)
              this.casheItems[formItem.name].classElement.isOpen = true;
          } else {
            formItem.listValues = [...(formItem.listLoad.subData ?? [])];
            if (formItem.isUnique) {
              formItem['listValuesCopy'] = formItem.listValues;
            }
            this.formGroup.patchValue({
              [formItem.name]: null
            });
          }
        }
        formArray.ArrayConfigration?.list?.forEach((item: any) => {
          item.forEach((innerItem: any) => {
            if (innerItem.name == formItem.name) {
              if (formItem.isUnique) {
                innerItem.listValuesCopy = formItem.listValues;
              }
              innerItem.listValues = formItem.listValues;
            }
          });
        });
      }
      return true;
    } catch (error: any) {
      return false;
    }
  }
  ngAfterContentChecked() {
    this.cdr.markForCheck(); // Marks the view for future change detection
  }
  ngOnChanges(changes: SimpleChanges): void {
    Logger.debug('changes field: ', changes);
    if ('ListLoadChanged' in changes) {
      if (this.formItem.listValues && this.formItem.listValues.length > 0 && this.formItem.listLoad) {
        if (this.formItem.isUnique) {
          this.formItem['listValuesCopy'] = this.validateList(this.formItem);
        } else {
          this.formItem.listValues = this.validateList(this.formItem);
        }
      }
    }
  }

  validateKeyInputTypeNum(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Meta'];
    // Allow: Ctrl+C, Ctrl+V, Ctrl+A, Ctrl+X
    const isCtrlCommand = (event.ctrlKey || event.metaKey) && ['c', 'v', 'a', 'x'].includes(event.key.toLowerCase());
    const target = event.target as HTMLInputElement; // Narrow down the type of event.target
    const isNumeric = /[0-9.]/.test(event.key); // Adjusted regex to allow decimals
    const isDecimalPoint = event.key === '.' && !target.value.includes('.'); // Check if it's a decimal point and not already in the value
    if (!(isNumeric || allowedKeys.includes(event.key) || isDecimalPoint || isCtrlCommand)) {
      event.preventDefault();
    }
  }

  validatePasteTypeNum(event: ClipboardEvent) {
    const pastedData = event.clipboardData?.getData('text');
    if (pastedData && !/^\d+(\.\d+)?$/.test(pastedData)) {
      event.preventDefault();
    }
  }

  execlude(data: any) {
    if (this.formItem.ArrayConfigration) {
      this.formItem.ArrayConfigration.list?.forEach((fields: any, index: number) => {
        if (index != data.index) {
          let field = fields.filter((field: any) => field.name == data.fieldName)[0];
          if (data.changedValue) {
            let changedValueIndex = field.execludedValue.indexOf(data.changedValue);
            changedValueIndex != -1 ? field.execludedValue.splice(changedValueIndex, 1) : '';
          }
          if (field.execludedValue) {
            field.execludedValue.push(data.value);
          } else {
            field.execludedValue = [data.value];
          }
          field.listValuesCopy = this.validateList(field);
        }
      });
    }
  }
  includeUniqueValue(data: any) {
    if (this.formItem.ArrayConfigration) {
      this.formItem.ArrayConfigration.list?.forEach((fields: any, index: number) => {
        if (index != data.index) {
          let field = fields.filter((field: any) => field.name == data.fieldName)[0];
          if (field.execludedValue) {
            field.execludedValue = field.execludedValue.filter((value: any) => value != data.value);
          } else {
            field.execludedValue = [data.value];
          }
          field.listValuesCopy = this.validateList(field);
        }
      });
    }
  }

  validateList(fieldConfig: any) {
    if (fieldConfig.execludedValue && fieldConfig.listValues) {
      return fieldConfig.listValues.filter((item: any) => fieldConfig.execludedValue.indexOf(item.value) == -1);
    } else {
      return fieldConfig.listValues;
    }
  }

  isSimpleItem(itemType: AllInputTypes): boolean {
    return [AllInputTypes.Text, AllInputTypes.Email, AllInputTypes.Password].indexOf(itemType) > -1;
  }

  changeValue(event: any, itemName: string) {
    const currentValue = this.formGroup.get(itemName)?.value;
    if (currentValue !== event) {
      this.formGroup.get(itemName)?.setValue(event);
    }
    return true;
  }

  async onRemoveFile(event: File[]) {
    this.formGroup.get(this.formItem.name)?.reset(null);
    if (event.length) this.onUploadSuccess(event);
  }

  async onUploadSuccess(event: any) {
    this.formGroup.get(this.formItem.name)?.setValue(event);
    // await this.fileUploadService.handleFileUpload(event, this.formItem, this.formGroup);
  }

  // Start Upload Single File Functions
  checkPermission() {
    // if (!this.hasCreateAttachmentsPermission) {
    //   this.toastService.showError(
    //     'Permission Denied',
    //     'You do not have permission to upload files.',
    //     false,
    //     5000
    //   );
    //   return false;
    // }
    return true;
  }

  triggerFileUpload() {
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  async changeFile(event: any) {
    // Check for permission before proceeding
    if (!this.checkPermission()) {
      return; // Stop if permission is denied
    }

    const newFiles = event.target.files;
    this.cdr.markForCheck(); // Marks the view for change detection
    setTimeout(() => {
      const file = newFiles[0];

      // Check if the file size exceeds the limit
      if (file.size > this.fileSizeLimit) {
        this.toastService.showWarning(
          'File Too Large',
          `The selected file "${file.name}" exceeds the size limit of ${(this.fileSizeLimit / (1024 * 1024)).toFixed(
            2
          )} MB. Please choose a smaller file.`,
          false,
          5000
        );
        return;
      }

      // Check if the new file matches the saved file (name and size)
      const duplicateFiles = Array.from(newFiles).filter(
        (f: any) => this.savedSingleFile && f.name === this.savedSingleFile.name && f.size === this.savedSingleFile.size
      );

      // Handle duplicate files
      if (duplicateFiles.length > 0) {
        const duplicateNames = duplicateFiles.map((f: any) => f.name).join(', ');
        this.toastService.showError(
          'Duplicate File',
          `The following file has already been uploaded: ${duplicateNames}`,
          false,
          5000
        );
        return;
      }

      // Proceed with file submission if no duplicates or size issues
      this.submitFile(newFiles, this.formItem);
    }, 0);
  }

  onDragOver(event: DragEvent) {
    // Check permission
    if (!this.checkPermission()) {
      return; // Stop further processing if permission is denied
    }

    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    // Check permission
    if (!this.checkPermission()) {
      return; // Stop further processing if permission is denied
    }

    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onFileDrop(event: DragEvent) {
    // Check permission
    if (!this.checkPermission()) {
      return; // Stop further processing if permission is denied
    }

    // You can add file handling logic here if necessary
    // const file = event.dataTransfer?.files[0];
    // if (file) {
    //   this.uploadFile(file);
    //   this.fileChanged.emit(file);
    // }
  }

  truncateFileName(name: string, maxLength: number = 15): string {
    if (name.length <= maxLength) {
      return name;
    }
    const extension = name.substring(name.lastIndexOf('.'));
    const baseName = name.substring(0, maxLength - extension.length);
    return `${baseName}...${extension}`;
  }

  formatSizeUnits(size: any): any {
    if (size >= 1073741824) {
      return (size / 1073741824).toFixed(2) + ' GB';
    } else if (size >= 1048576) {
      return (size / 1048576).toFixed(2) + ' MB';
    } else if (size >= 1024) {
      return (size / 1024).toFixed(2) + ' KB';
    } else if (size > 1) {
      return size + ' bytes';
    } else if (size === 1) {
      return size + ' byte';
    } else {
      return '0 bytes';
    }
  }
  // Method to dynamically set the accept attribute
  setAcceptExtensions(fileConfig: any): any {
    // Map allowed extensions to a valid accept string (e.g., '.jpg, .png, .pdf')
    let acceptExtensions = fileConfig?.allowedExtension.map((ext: any) => `.${ext}`).join(',');
    return acceptExtensions;
  }
  removeFile(formItem?: any): void {
    if (this.savedSingleFile?.objectURL) {
      URL.revokeObjectURL(this.savedSingleFile.objectURL); // Clean up objectURL
    }
    this.savedSingleFile = null;
    formItem.addedBforeItems = null; // Reset the addedBforeItems
    this.formGroup.get(formItem.name)?.setValue(null); // Reset the form control value
  }

  downloadFile(file: any) {
    this.onDownloadFile.emit(file?.addedBforeItems?.id);
  }
  async submitFile(data: any, formItem: FormInputConfig) {
    // if (!this.hasCreateAttachmentsPermission) {
    //   this.toastService.showError(
    //     'Permission Denied',
    //     'You do not have permission to upload files.',
    //     false,
    //     5000
    //   );
    //   return; // Stop further processing if permission is denied
    // }

    const formData = new FormData();
    formData.append('files', data[0]);
    try {
      this.isLoadingUploadFile = true;
      this.dynamicLoadDataService.setBody({ data: formData });
      const res = await this.dynamicLoadDataService.post({ path: URLS.ATTACHMENTS });

      // Store file data, including URLs, in savedSingleFile
      this.savedSingleFile = {
        name: data[0]?.name,
        size: data[0]?.size,
        type: data[0]?.type,
        fileUrl: res[0], // Assuming res[0] contains the uploaded file URL from server
        objectURL: data[0]?.type?.includes('image') ? URL?.createObjectURL(data[0]) : null // Create objectURL only for images
      };

      // Set the form control's value to the server file URL
      this.formGroup.get(formItem.name)?.setValue(res[0]);

      this.isLoadingUploadFile = false;
    } catch (errors: ErrorOrError[] | unknown | any) {
      handleApiErrors(
        createValidationError('Validation Error', `${errors[0]?.description}`, 'file'),
        this.toastService
      );
      this.isLoadingUploadFile = false;
    }
  }
  // End Upload Single File Functions

  getError(formItem: FormInputConfig): string {
    let error: string = '';
    if (formItem.validation) {
      const controller = this.formGroup.get(formItem.name);
      if (controller?.touched && controller.invalid) {
        let allErrors = controller?.errors;
        formItem.validation.forEach((validation: FormValidation) => {
          if (
            allErrors &&
            (allErrors[validation.function.name] || allErrors[validation.errorName ?? '']) &&
            error == ''
          ) {
            error = validation.errorMessage;
            this.validationErrors = {};
          }
        });
      }
    }
    error = this.liveTranslate?.lang?.[error] ?? error;
    if (this.validationErrors?.[formItem?.name]) {
      error += ' ' + this.validationErrors?.[formItem?.name].join(' , ');
    }

    if (formItem.name === 'confirmPassword' && this.formGroup.get('confirmPassword')?.errors?.['mismatch']) {
      error = 'Passwords do not match';
    }
    return error;
  }
  changeTouche(itemName: string) {
    this.formGroup.get(itemName)?.markAsTouched();
    return true;
  }

  labelClick(event: any, formItem: FormInputConfig) {
    if (formItem.enabilLabelClick) {
      event.preventDefault();

      this.onLabelClick.emit(formItem);
    }
    return true;
  }

  stopPaste(event: any, formItem: FormInputConfig) {
    if (formItem.stopPaste) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  getSavedData(formItem: FormInputConfig) {
    if (typeof formItem.savedValue == 'string') {
      if (formItem.savedValue && formItem.savedValue.indexOf('.pdf') > -1) {
        return `<a class="saved-attachment" href="${formItem.savedValue}" target="_blank"><i class="mdi mdi-file-pdf-box"></i></a>`;
      } else {
        return `<a class="saved-attachment" href="${formItem.savedValue}" target="_blank"><img src="${formItem.savedValue}" alt=""></a>`;
      }
    } else {
      let html = formItem.savedValue?.map((v: any) => {
        if (v && v.indexOf('.pdf') > -1) {
          return `<a class="saved-attachment" href="${v}" target="_blank"><i class="mdi mdi-file-pdf-box"></i></a>`;
        }
        return `<a class="saved-attachment" href="${v}" target="_blank"><img src="${v}" alt=""></a>`;
      });
      return html?.toString();
    }
  }

  onSearch(event: any) {
    if (this.formItem.dynamicLoad) {
      if (!this.casheItems[this.formItem.name]) {
        this.casheItems[this.formItem.name] = document.getElementById(this.formItem.name);
        fromEvent(this.casheItems[this.formItem.name], 'input')
          .pipe(
            map((value: any) => {
              return (value.target as HTMLInputElement).value;
            })
          )
          .pipe(debounceTime(1000))
          // .pipe(distinctUntilChanged())
          .subscribe((searchTerm) => {
            if (this.formItem.loadOnScroll) {
              searchTerm.trim()
                ? this.getDynamicLoadData(searchTerm, 1)
                : [(this.scrollData.reset = true), this.getDynamicLoadData('', 1)];
            } else {
              searchTerm.trim() ? this.getDynamicLoadData(searchTerm) : null;
            }
          });
      }
    }
    return true;
  }

  async getDynamicLoadData(searchTerm: string, currentPage?: number) {
    try {
      let currentFieldConfig = this.formItem;
      let params: any;
      let isLoad: boolean = true;
      if (this.formItem.type == AllInputTypes.Auto_complete_text) {
        isLoad = false;
      }
      if (currentFieldConfig?.dynamicLoad) {
        params = {
          ...(currentFieldConfig?.dynamicLoad.params ? currentFieldConfig?.dynamicLoad.params : {}),
          search: searchTerm
        };
        if (currentFieldConfig.loadOnScroll) {
          if (currentPage) this.scrollData.currentPage = currentPage;
          if (this.scrollData.currentPage > this.scrollData.totalPages) return;
          if (!searchTerm) searchTerm = '';
          if (!this.scrollData.loading) this.scrollData.loading = true;
          isLoad = false;
          params = {
            ...(currentFieldConfig?.dynamicLoad.params ? currentFieldConfig?.dynamicLoad.params : {}),
            page: this.scrollData.currentPage,
            search: searchTerm
          };
        }

        // (currentFieldConfig?.dynamicLoad.loadService ?? this.dynamicLoadDataService).setPath(currentFieldConfig?.dynamicLoad?.url);
        // (currentFieldConfig?.dynamicLoad.loadService ?? this.dynamicLoadDataService).setParams({ search: searchTerm, ...(currentFieldConfig?.dynamicLoad.params ? currentFieldConfig?.dynamicLoad.params : {}) })
        let res: any;
        this.dynamicSelect?.close();
        currentFieldConfig?.dynamicLoad?.type == 'get'
          ? (res = await (currentFieldConfig?.dynamicLoad.loadService ?? this.dynamicLoadDataService).get({
              isLoad,
              params: params,
              path: currentFieldConfig?.dynamicLoad?.url
            }))
          : (res = await (currentFieldConfig?.dynamicLoad.loadService ?? this.dynamicLoadDataService).post({
              isLoad,
              params: params,
              path: currentFieldConfig?.dynamicLoad?.url
            }));
        if (currentFieldConfig.dynamicLoad.responseDataPrepare) {
          res.data = currentFieldConfig.dynamicLoad.responseDataPrepare(
            res[currentFieldConfig.dynamicLoad?.arrayAttributeName]
          );
        }
        if (currentFieldConfig.loadOnScroll) {
          this.scrollData.totalPages = res.meta.last_page;
          this.scrollData.totalCount = res.meta.total;
          res.data = res.data.map((item: any) => {
            return {
              ...item,
              value: item[currentFieldConfig?.dynamicLoad?.valueKey || ''],
              label: item[currentFieldConfig?.dynamicLoad?.labelKey || '']
            };
          });
          if (searchTerm || this.scrollData.reset) {
            this.formItem.listValues = res.data;
          } else {
            this.formItem.listValues = [...(this.formItem.listValues ?? []), ...res.data];
          }
          if (this.formItem.isUnique) {
            this.formItem['listValuesCopy'] = this.validateList(this.formItem);
          } else {
            this.formItem.listValues = this.validateList(this.formItem);
          }
          this.scrollData.currentPage++;
          this.scrollData.reset = false;
          this.scrollData.loading = false;
        } else {
          if (res[currentFieldConfig?.dynamicLoad?.arrayAttributeName]) {
            if (res[currentFieldConfig?.dynamicLoad?.arrayAttributeName].length) {
              this.formItem.listValues = [
                ...(currentFieldConfig.dynamicLoad.subData ?? []),
                ...(currentFieldConfig?.dynamicLoad.dataPrepare
                  ? currentFieldConfig?.dynamicLoad.dataPrepare(
                      res[currentFieldConfig?.dynamicLoad?.arrayAttributeName]
                    )
                  : res[currentFieldConfig?.dynamicLoad?.arrayAttributeName].map((item: any) => {
                      if (this.formItem.hasSelectAll) {
                        return {
                          value: item[currentFieldConfig?.dynamicLoad?.valueKey || ''],
                          label: item[currentFieldConfig?.dynamicLoad?.labelKey || ''],
                          selectedAllGroup: 'selectedAllGroup'
                        };
                      } else {
                        return {
                          value: item[currentFieldConfig?.dynamicLoad?.valueKey || ''],
                          label: item[currentFieldConfig?.dynamicLoad?.labelKey || '']
                        };
                      }
                    }))
              ];
              this.formItem['isOpen'] = true;
              if (this.casheItems[this.formItem.name] && this.casheItems[this.formItem.name].classElement)
                this.casheItems[this.formItem.name].classElement.isOpen = true;
              // this.d_select!.items = this.formItem.listValues ?? []
              if (this.formItem.type === 'select' || this.formItem.type === 'multiSelect') {
                this.dynamicSelect!.searchTerm = searchTerm;
                this.cdr.markForCheck(); // Marks the view for future change detection
                this.dynamicSelect?.open();
              }
              if (this.formItem.isUnique) {
                this.formItem['listValuesCopy'] = this.validateList(this.formItem);
              } else {
                this.formItem.listValues = this.validateList(this.formItem);
              }
              return this.formItem.listValues;
            } else {
              this.dynamicSelect!.searchTerm = searchTerm;
              this.formItem.listValues = [...(currentFieldConfig.dynamicLoad.subData ?? [])];
              if (this.formItem.isUnique) {
                this.formItem['listValuesCopy'] = this.validateList(this.formItem);
              } else {
                this.formItem.listValues = this.validateList(this.formItem);
              }
              // this.formGroup.patchValue({
              //   [this.formItem.name]: null
              // });
              // this.formItem.isOpen = false;
            }
          }
        }
      }
      return this.formItem.listValues;
    } catch (error: any) {
      return [];
    }
  }

  customSearchFn(term: string, item: any) {
    return true;
  }

  customSearchFn2(term: string, item: any) {
    term = term.toLocaleLowerCase();
    return item.label.trim().toLocaleLowerCase().indexOf(term) > -1 || item['searchKey']?.includes(term);
  }

  // start Typeahead autocomplete
  autoCompleteSearch: OperatorFunction<string, any> = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      switchMap(async (term: string) => (term.trim() !== '' ? await this.getDynamicLoadData(term) : null))
    );

  formatter = (x: { label: string; value: any }) => x.label;

  selectedItem(item: any, formItem: FormInputConfig) {
    this.formGroup.get(formItem.name)?.setValue(item.item.value);
    return true;
  }

  // end Typeahead autocomplete

  addFormArray() {
    this.onAddFormArray.emit(true);
    return true;
  }
  addGroupFormArray(index: number) {
    this.onGroupAddFormArray.emit(index);
    return true;
  }
  removeFormArray(index: number) {
    // Logger.debug(index,this.formGroup,this.formItem.name)
    // Logger.debug(this.formGroup.value[this.formItem.name][index])
    if (this.formItem.ArrayConfigration?.fieldsUnique) {
      let value = this.formGroup.value[this.formItem.name][index];
      let filedsUnique: any = this.formItem.ArrayConfigration?.fieldsUnique;
      Object.keys(filedsUnique).forEach((uniqueField) => {
        if (value[uniqueField]) {
          this.formSubs.includeValue.next({
            index: index,
            value: value[uniqueField],
            fieldName: uniqueField
          });
        }
      });
    }
    this.onRemoveFormArray.emit(index);
    return true;
  }
  removeGroupFormArray(index: number, groupIndex: number) {
    this.onGroupRemoveFormArray.emit({ index, groupIndex });
    return true;
  }

  getFormArray(indexForm: number): UntypedFormGroup {
    return (this.formGroup.get(this.formItem.name) as UntypedFormArray).controls[indexForm] as UntypedFormGroup;
  }

  locationChange(emittedLocation: LocationModel | any, formItemName: string) {
    Logger.debug('Emitted Location:', emittedLocation);
    this.formGroup.get(formItemName)?.patchValue(emittedLocation, { emitEvent: true });
    return true;
  }

  onAddChip(event: any, formItemName: string) {
    // this.formGroup.get(formItemName)?.patchValue([...this.formGroup.get(formItemName)?.value, event.value])
  }

  // Start Mobile Number Functions
  allowOnlyNumbers(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode;
    // Allow only digits (0-9)
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }
  preventNonNumericPaste(event: ClipboardEvent): void {
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    const pastedData = clipboardData.getData('text');
    // Block if pasted data contains non-numeric characters
    if (!/^\d+$/.test(pastedData)) {
      event.preventDefault();
    }
  }
  // End Mobile Number Functions

  // Add New Button Function
  addNew(formItem: FormInputConfig): void {
    this.onAddNew.emit(formItem);
  }

  // Start Multi Select Tree
  onNodeSelect(event: any, fieldName: string): void {
    Logger.debug('Node selected:', event.node);
    Logger.debug('Node selectedfield name:', fieldName);
    // Custom logic for handling node selection
  }
  onNodeUnselect(event: any, fieldName: string): void {
    Logger.debug('Node unselected:', event.node);
    Logger.debug('Node unselected field name:', fieldName);
    // Custom logic for handling node deselection
  }
  // End Multi Select Tree
}
